import {
  ArrowUpTrayIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy/button';

import { FileUploadDropzoneProps } from './types';
import useDropzone from './useDropzone';

function FileUploadDropzone({
  accept,
  maxSize,
  onSelect,
  title,
  description,
  className,
  ...props
}: FileUploadDropzoneProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'fileUpload' });
  const {
    inputRef,
    isDragging,
    isRejected,
    message = { title, description },
    openFilePicker,
    handleChange,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDropzone({ accept, maxSize, onSelect });

  return (
    <div
      {...props}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={twMerge(
        'flex flex-col items-center gap-2 rounded-2xl border border-dashed border-base-300 bg-base-200 px-6 py-8 text-center transition-colors',
        isDragging && 'border-primary bg-primary-content',
        isRejected && 'border-error bg-error/5',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        aria-label={t('selectFile')}
        onChange={handleChange}
      />
      <span className="mb-4 flex size-14 items-center justify-center rounded-full bg-base-100">
        <ConditionalRenderer
          condition={isRejected}
          fallback={<ArrowUpTrayIcon className="size-5 text-primary" />}
        >
          <ExclamationCircleIcon className="size-5 text-error" />
        </ConditionalRenderer>
      </span>
      <p className="font-semibold text-base-content">{message.title}</p>
      <ConditionalRenderer condition={message.description}>
        <p className="text-sm text-base-content/70">{message.description}</p>
      </ConditionalRenderer>
      <Button
        type="button"
        size="sm"
        color="primary"
        variant="soft"
        className={twMerge('mt-4', isDragging && 'invisible')}
        onClick={openFilePicker}
      >
        <ArrowUpTrayIcon className="size-4" />
        <ConditionalRenderer
          condition={isRejected}
          fallback={t('selectFile')}
        >
          {t('selectAnotherFile')}
        </ConditionalRenderer>
      </Button>
    </div>
  );
}

export default FileUploadDropzone;
